import {
  LiveRecordingSession,
  describeRecordingFailure,
  selectRecordingMime,
  type LiveRecordingConfig,
  type LiveRecordingStatus,
  type MimeCandidate,
} from '../../visual/index.js';
import { CanvasAudioStream } from './CanvasAudioStream.js';
import {
  MediaRecorderAdapter,
  mediaRecorderAvailable,
  probeRecordingMime,
} from './MediaRecorderAdapter.js';

/**
 * Assembles a live recording for this browser, or explains why it cannot.
 *
 * The one place that knows a `CanvasAudioStream` and a `MediaRecorderAdapter`
 * belong together, matching how `createAvExport` pairs the frame source with the
 * encoder. Everything that can fail — no recorder, no capture, no format the
 * browser will write — is answered here, before the user presses anything, so a
 * recording that cannot happen is refused while it is still being asked for
 * rather than partway through a run.
 */

export interface LiveRecordingBuild {
  readonly session: LiveRecordingSession;
  readonly stream: CanvasAudioStream;
  readonly recorder: MediaRecorderAdapter;
  readonly candidate: MimeCandidate;
}

export type LiveRecordingAttempt =
  | ({ readonly ok: true } & LiveRecordingBuild)
  | { readonly ok: false; readonly message: string };

export interface CreateLiveRecordingOptions {
  readonly canvas: HTMLCanvasElement;
  readonly config: LiveRecordingConfig;
  /** Everything the player hears. Connected to, never rerouted. */
  readonly audioSource?: AudioNode | null;
  readonly audioContext?: AudioContext | null;
  readonly onStatus?: (status: LiveRecordingStatus) => void;
  readonly videoBitsPerSecond?: number;
}

export function createLiveRecording(
  options: CreateLiveRecordingOptions,
): LiveRecordingAttempt {
  if (!mediaRecorderAvailable()) {
    return { ok: false, message: describeRecordingFailure('no-recorder') };
  }

  const candidate = selectRecordingMime(probeRecordingMime, { audio: options.config.audio });
  if (candidate === null) {
    return { ok: false, message: describeRecordingFailure('no-mime') };
  }

  const built = CanvasAudioStream.create({
    canvas: options.canvas,
    fps: options.config.fps,
    // Audio off means the branch is never made at all, rather than made and
    // muted — a muted branch would still cost a node in the graph.
    audioSource: options.config.audio ? options.audioSource ?? null : null,
    audioContext: options.config.audio ? options.audioContext ?? null : null,
  });
  if (!built.ok) {
    return { ok: false, message: describeRecordingFailure(built.reason, built.message) };
  }

  const stream = built.stream;
  let recorder: MediaRecorderAdapter;
  const holder: { session: LiveRecordingSession | null } = { session: null };
  try {
    recorder = new MediaRecorderAdapter({
      stream: stream.mediaStream,
      mimeType: candidate.mime,
      ...(options.videoBitsPerSecond === undefined
        ? {}
        : { videoBitsPerSecond: options.videoBitsPerSecond }),
      callbacks: {
        onChunk: (chunk) => holder.session?.acceptChunk(chunk),
        onStop: () => holder.session?.acceptStop(),
        onError: (message) => holder.session?.acceptError(message),
      },
    });
  } catch (cause) {
    stream.release();
    return {
      ok: false,
      message: describeRecordingFailure(
        'start-failed',
        cause instanceof Error ? cause.message : String(cause),
      ),
    };
  }

  const session = new LiveRecordingSession({
    recorder,
    source: stream,
    config: options.config,
    extension: candidate.extension,
    ...(options.onStatus === undefined ? {} : { onStatus: options.onStatus }),
  });
  holder.session = session;

  return { ok: true, session, stream, recorder, candidate };
}
